import React, { useState, useEffect } from 'react';

import { validateInputs } from '@trqk/hooks';

import {
    Input,
    Dropdown,
    Button,
    Tooltip,
    Toggle,
    IconInfoCircle,
    InputValidation,
} from '../..';
import { inputIds } from '../constants';

interface LogsInputFormProps {
    submitHandler: (data: Record<string, unknown>) => void;
}

/**
 * Renders a form to get eventbridge log display parameters from the user
 * @param   {LogsInputFormProps} An object containing a submitHandler function, which is used to pass form data to the parent component
 * @returns {JSX.Element} The JSX representation of the input form
 */
export const LogsInputForm = ({
    submitHandler,
}: LogsInputFormProps): JSX.Element => {
    const [id, setId] = useState('');
    const [maxResults, setMaxResults] = useState('1000');
    const [eventType, setEventType] = useState('');
    const [eventStatus, setEventStatus] = useState('');
    const [rangeValue, setRangeValue] = useState('1');
    const [rangeUnit, setRangeUnit] = useState('weeks');
    const now = new Date();
    const [year, setYear] = useState(now.getUTCFullYear().toString());
    const [month, setMonth] = useState((now.getUTCMonth() + 1).toString());
    const [day, setDay] = useState(now.getUTCDate().toString());
    const [hour, setHour] = useState(now.getUTCHours().toString());
    const [minute, setMinute] = useState(now.getUTCMinutes().toString());
    const [second, setSecond] = useState(now.getUTCSeconds().toString());

    const [idWasEdited, setIdWasEdited] = useState(false);
    const [eventTypeWasEdited, setEventTypeWasEdited] = useState(false);
    const [maxResultsWasEdited, setMaxResultsWasEdited] = useState(false);
    const [rangeWasEdited, setRangeWasEdited] = useState(false);
    const [timeWasEdited, setTimeWasEdited] = useState(false);

    const [filterByDate, setFilterByDate] = useState(false);

    const [hasValidationErrors, setHasValidationErrors] = useState(false);
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const formIsDirty =
        idWasEdited ||
        eventTypeWasEdited ||
        maxResultsWasEdited ||
        timeWasEdited ||
        rangeWasEdited;
    const showInputValidation = formIsDirty && hasValidationErrors;
    const disableFormSubmission = hasValidationErrors;

    const handleSubmit = (): void => {
        submitHandler({
            userId: id,
            eventType,
            eventStatus,
            maxResults,
            datetime: { year, month, day, hour, minute, second },
            range: { rangeValue, rangeUnit },
            filterByDate,
        });
    };

    useEffect(() => {
        if (formIsDirty) {
            const validationResults = validateInputs([
                {
                    inputId: inputIds.logsUserId,
                    label: 'User ID',
                    type: 'listObjectsInS3UserId',
                    value: id,
                    required: false,
                },
                {
                    inputId: inputIds.logsMaxResults,
                    label: 'Max results',
                    type: 'listObjectsInS3MaxResults',
                    value: maxResults,
                    required: false,
                },
                {
                    inputId: inputIds.logsDateYear,
                    label: 'Year',
                    type: 'dateYear',
                    value: year,
                    required: filterByDate,
                },
                {
                    inputId: inputIds.logsDateMonth,
                    label: 'Month',
                    type: 'dateMonth',
                    value: month,
                    required: filterByDate,
                },
                {
                    inputId: inputIds.logsDateDay,
                    label: 'Day',
                    type: 'dateDay',
                    value: day,
                    required: filterByDate,
                },
                {
                    inputId: inputIds.logsDateHours,
                    label: 'Hour',
                    type: 'dateHours',
                    value: hour,
                    required: filterByDate,
                },
                {
                    inputId: inputIds.logsDateMinutes,
                    label: 'Minutes',
                    type: 'dateMinutes',
                    value: minute,
                    required: filterByDate,
                },
                {
                    inputId: inputIds.logsDateSeconds,
                    label: 'Seconds',
                    type: 'dateSeconds',
                    value: second,
                    required: filterByDate,
                },
            ]);

            setHasValidationErrors(validationResults.hasValidationErrors);
            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [
        formIsDirty,
        id,
        maxResults,
        year,
        month,
        day,
        hour,
        minute,
        second,
        filterByDate,
        hasValidationErrors,
    ]);

    return (
        <form className="flex-1 w-full mr-8">
            <div>
                User ID:
                <div className="w-64 text-sm">
                    <Input
                        inputId={inputIds.logsUserId}
                        value={id}
                        handleChange={(value: string): void => {
                            setId(value);
                        }}
                        handleBlur={(): void => setIdWasEdited(true)}
                    />
                </div>
            </div>
            <div>
                Event Type:
                <div className="w-64 text-sm">
                    <Input
                        inputId={inputIds.logsEventType}
                        value={eventType}
                        handleChange={(value: string): void => {
                            setEventType(value);
                        }}
                        handleBlur={(): void => setEventTypeWasEdited(true)}
                    />
                </div>
            </div>
            Event Status:
            <Dropdown
                defaultIndex={0}
                options={[
                    {
                        label: 'Any',
                        value: '',
                    },
                    {
                        label: 'success',
                        value: 'success',
                    },
                    {
                        label: 'pending',
                        value: 'pending',
                    },
                    {
                        label: 'failure',
                        value: 'failure',
                    },
                ]}
                size="medium"
                handleChange={(nextValue: string): void => {
                    setEventStatus(nextValue);
                }}
                width={200}
            />
            <div>
                Max Results:
                <div className="w-20">
                    <Input
                        inputId={inputIds.logsMaxResults}
                        value={maxResults}
                        handleChange={(value: string): void => {
                            setMaxResults(value);
                        }}
                        handleBlur={(): void => setMaxResultsWasEdited(true)}
                    />
                </div>
            </div>
            <br />
            <Toggle
                inputId="Date Filter Toggle"
                label="Filter results by date (UTC)"
                onChange={(): void => {
                    setFilterByDate(!filterByDate);
                }}
                checked={filterByDate}
            />
            <div
                className={
                    filterByDate ? 'dateFilter' : 'dateFilter opacity-25'
                }
            >
                <div className="pt-4">
                    <div className="w-16 inline-block">
                        MM
                        <Input
                            inputId={inputIds.logsDateMonth}
                            disabled={!filterByDate}
                            value={month}
                            handleChange={(value: string): void => {
                                setMonth(value);
                            }}
                            handleBlur={(): void => {
                                setTimeWasEdited(true);
                            }}
                        />
                    </div>
                    /
                    <div className="w-16 inline-block">
                        DD
                        <Input
                            inputId={inputIds.logsDateDay}
                            disabled={!filterByDate}
                            value={day}
                            handleChange={(value: string): void => {
                                setDay(value);
                            }}
                            handleBlur={(): void => {
                                setTimeWasEdited(true);
                            }}
                        />
                    </div>
                    /
                    <div className="w-20 inline-block">
                        YYYY
                        <Input
                            inputId={inputIds.logsDateYear}
                            disabled={!filterByDate}
                            value={year}
                            addClasses=""
                            handleChange={(value: string): void => {
                                setYear(value);
                            }}
                            handleBlur={(): void => {
                                setTimeWasEdited(true);
                            }}
                        />
                    </div>
                </div>
                <div className="pt-4">
                    <div className="w-12 inline-block">
                        HH
                        <Input
                            inputId={inputIds.logsDateHours}
                            disabled={!filterByDate}
                            value={hour}
                            handleChange={(value: string): void => {
                                setHour(value);
                            }}
                            handleBlur={(): void => {
                                setTimeWasEdited(true);
                            }}
                        />
                    </div>
                    :
                    <div className="w-12 inline-block">
                        MM
                        <Input
                            inputId={inputIds.logsDateMinutes}
                            disabled={!filterByDate}
                            value={minute}
                            handleChange={(value: string): void => {
                                setMinute(value);
                            }}
                            handleBlur={(): void => {
                                setTimeWasEdited(true);
                            }}
                        />
                    </div>
                    :
                    <div className="w-12 inline-block">
                        SS
                        <Input
                            inputId={inputIds.logsDateSeconds}
                            disabled={!filterByDate}
                            value={second}
                            handleChange={(value: string): void => {
                                setSecond(value);
                            }}
                            handleBlur={(): void => {
                                setTimeWasEdited(true);
                            }}
                        />
                    </div>
                </div>

                <div className="pt-4">
                    <p>Search Range</p>
                    <p>(leading up to above timestamp)</p>
                    <div className="w-16 inline-block">
                        <Input
                            inputId={inputIds.logsRangeValue}
                            disabled={!filterByDate}
                            value={rangeValue}
                            handleChange={(value: string): void => {
                                setRangeValue(value);
                            }}
                            handleBlur={(): void => {
                                setRangeWasEdited(true);
                            }}
                        />
                    </div>

                    <div className="">
                        <Dropdown
                            defaultIndex={2}
                            disabled={!filterByDate}
                            options={[
                                {
                                    label: 'Years',
                                    value: 'years',
                                },
                                {
                                    label: 'Months',
                                    value: 'months',
                                },
                                {
                                    label: 'Weeks',
                                    value: 'weeks',
                                },
                                {
                                    label: 'Days',
                                    value: 'days',
                                },
                                {
                                    label: 'Hours',
                                    value: 'hours',
                                },
                                {
                                    label: 'Minutes',
                                    value: 'minutes',
                                },
                                {
                                    label: 'Seconds',
                                    value: 'seconds',
                                },
                            ]}
                            size="medium"
                            handleChange={(nextValue: string): void => {
                                setRangeUnit(nextValue);
                            }}
                            width={200}
                        />
                    </div>
                </div>
            </div>
            <div className="flex">
                <div className="w-48">
                    <Button
                        addClasses="mt-4"
                        color="primary-light-bg"
                        handleClick={handleSubmit}
                        disabled={disableFormSubmission}
                    >
                        Submit
                    </Button>
                </div>
                {showInputValidation && (
                    <Tooltip
                        message={
                            <InputValidation
                                validationOutputs={validationOutputs}
                            />
                        }
                    >
                        <span className="text-sm pl-4 text-highlight-orange font-semibold">
                            Something&rsquo;s not right.
                        </span>
                        <IconInfoCircle
                            addClasses="inline-block ml-1"
                            fillColor="highlight-orange"
                        />
                    </Tooltip>
                )}
            </div>
        </form>
    );
};
